import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import "./Dropdown.css";

const MENU_GAP = 8;
const VIEWPORT_MARGIN = 8;
const MAX_MENU_HEIGHT = 320;

function Dropdown({ options = [], value = "", onChange, name, id, placeholder = "Select", disabled, showLegend = false }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const generatedIdRef = useRef(`dropdown-${Math.random().toString(36).slice(2, 9)}`);

  const normalized = useMemo(() => (
    (Array.isArray(options) ? options : []).map((option) => ({
      label: option.label ?? String(option.value ?? option.id ?? ""),
      value: option.value ?? option.id ?? String(option.label ?? "")
    }))
  ), [options]);

  const selected = normalized.find((option) => String(option.value) === String(value));
  const listboxId = `${id || generatedIdRef.current}-listbox`;
  const optionId = (index) => `${listboxId}-option-${index}`;

  const close = () => setOpen(false);

  const openMenu = () => {
    if (disabled) return;
    const selectedIndex = normalized.findIndex((option) => String(option.value) === String(value));
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : (normalized.length > 0 ? 0 : -1));
    setOpen(true);
  };

  const toggle = () => {
    if (open) close();
    else openMenu();
  };

  const fireChange = (newValue) => {
    if (typeof onChange === "function") {
      onChange({ target: { name, value: newValue } });
    }
  };

  const updateMenuPosition = useCallback(() => {
    const trigger = btnRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = Math.min(rect.width, viewportWidth - VIEWPORT_MARGIN * 2);
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      Math.max(VIEWPORT_MARGIN, viewportWidth - menuWidth - VIEWPORT_MARGIN)
    );
    const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_MARGIN - MENU_GAP;
    const spaceAbove = rect.top - VIEWPORT_MARGIN - MENU_GAP;
    const desiredHeight = Math.min(MAX_MENU_HEIGHT, Math.max(48, normalized.length * 41 + 16));
    const opensUpward = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
    const availableHeight = Math.max(48, opensUpward ? spaceAbove : spaceBelow);

    setMenuStyle({
      position: "fixed",
      left,
      width: menuWidth,
      maxHeight: Math.min(MAX_MENU_HEIGHT, availableHeight),
      top: opensUpward ? "auto" : rect.bottom + MENU_GAP,
      bottom: opensUpward ? viewportHeight - rect.top + MENU_GAP : "auto",
      zIndex: 30000
    });
  }, [normalized.length]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    const reposition = () => updateMenuPosition();
    window.addEventListener("resize", reposition);
    document.addEventListener("scroll", reposition, true);

    return () => {
      window.removeEventListener("resize", reposition);
      document.removeEventListener("scroll", reposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideMouseDown = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        btnRef.current && !btnRef.current.contains(event.target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () => document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [open]);

  const onKeyDown = (event) => {
    if (!open && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      openMenu();
      return;
    }
    if (!open) return;

    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }

    if (normalized.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index + 1 + normalized.length) % normalized.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + normalized.length) % normalized.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const option = normalized[activeIndex];
      if (option) {
        fireChange(option.value);
        close();
      }
    }
  };

  const onOptionClick = (option, index) => {
    fireChange(option.value);
    setActiveIndex(index);
    close();
  };

  const menu = open && menuStyle && typeof document !== "undefined"
    ? createPortal(
        <ul
          id={listboxId}
          className="dd-menu"
          role="listbox"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          tabIndex={-1}
          ref={menuRef}
          style={menuStyle}
          onKeyDown={onKeyDown}
        >
          {normalized.map((option, index) => (
            <li
              id={optionId(index)}
              key={`${String(option.value)}-${index}`}
              role="option"
              aria-selected={String(option.value) === String(value)}
              className={`dd-option ${index === activeIndex ? "active" : ""} ${String(option.value) === String(value) ? "selected" : ""}`}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => onOptionClick(option, index)}
            >
              {option.label}
            </li>
          ))}
        </ul>,
        document.body
      )
    : null;

  return (
    <>
      <div className={`dd-wrapper ${disabled ? "dd-disabled" : ""}`}>
        <button
          ref={btnRef}
          type="button"
          id={id}
          name={name}
          className={`dd-btn ${selected ? "selected" : ""}`}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={onKeyDown}
          disabled={disabled}
        >
          <span className={`dd-label ${selected ? "has-value" : ""}`}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown size={16} />
        </button>

        {showLegend && placeholder && <span className="dd-legend">{placeholder}</span>}
      </div>
      {menu}
    </>
  );
}

export default Dropdown;
